import React, { useCallback, memo, useEffect } from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import styles from './styles/Modal.module.css';

const Modal = ({ isOpen, onClose, children, title, isLarge }) => {
    const handleOverlayClick = useCallback(e => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);

    const handleEscapeKey = useCallback(e => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleEscapeKey]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={styles.overlay}
                        onClick={handleOverlayClick}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                    />
                    <motion.div
                        className={`${styles.modal} ${isLarge ? styles.modalLarge : ''}`}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        style={{ translateX: '-50%', translateY: '-50%' }}
                    >
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>
                        <div className={styles.content} data-modal-boundary>
                            {title && <h2 className={styles.title}>{title}</h2>}
                            {children}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default memo(Modal);
